import {CssBaseline} from '@mui/material';
import {configureApiUrl, resetApiClient, UserProvider} from '@smartmixin/core';
import ReactGA from 'react-ga4';
import {HelmetProvider} from 'react-helmet-async';
import {Provider as StoreProvider} from 'react-redux';
import {RouterProvider} from 'react-router';
import {PersistGate} from 'redux-persist/integration/react';
import {LanguageProvider, deviceLanguage} from './i18n';
import {SentryUtils} from './lib';
import UserSyncProvider from './lib/UserSyncProvider';
import router from './navigation/router';
import store, {persistor} from './store';

// Apply persisted debug API URL after Redux rehydration
const applyDebugApiUrl = () => {
  const debugApiUrl = store.getState().user?.debugApiUrl;
  if (debugApiUrl) {
    configureApiUrl(debugApiUrl);
    resetApiClient();
  }
};
import {ThemeProvider} from './theme';

// Configure API URL from environment variable (for E2E testing with mock server)
if (import.meta.env.VITE_API_URL) {
  configureApiUrl(import.meta.env.VITE_API_URL);
}

if (import.meta.env.VITE_GA_ID) {
  ReactGA.initialize(import.meta.env.VITE_GA_ID);
}

if (process.env.NODE_ENV !== 'development') {
  SentryUtils.configure();
}

// Swap favicon for staging environment
if (import.meta.env.VITE_IS_STAGING === 'true') {
  document
    .querySelectorAll<HTMLLinkElement>('link[rel*="icon"]')
    .forEach((link) => {
      link.href = link.href.replace('favicon.png', 'favicon-staging.png');
      link.href = link.href.replace('favicon.ico', 'favicon-staging.png');
    });
}

const is24 =
  new Date(2014, 1, 1, 15, 0, 0, 0).toLocaleTimeString().indexOf('15') > -1;

export default function App() {
  return (
    <StoreProvider store={store}>
      <PersistGate loading={null} persistor={persistor} onBeforeLift={applyDebugApiUrl}>
        <UserProvider
          deviceLanguage={deviceLanguage}
          uses24HourClock={is24}
          usesMetricSystem={true}>
          <ThemeProvider>
            <CssBaseline />
            <LanguageProvider>
              <HelmetProvider>
                <UserSyncProvider>
                  <RouterProvider router={router} />
                </UserSyncProvider>
              </HelmetProvider>
            </LanguageProvider>
          </ThemeProvider>
        </UserProvider>
      </PersistGate>
    </StoreProvider>
  );
}
